// 股市页的 K 线(日线、周线、月线):蜡烛、均线、成交额;鼠标停上去(手机上点一下)看这一根的数;长按再拖选一段看区间累计;
// 看哪一段:拖动平移、滚轮或触控板捏合缩放、触控板左右滑平移、手机双指捏合缩放单指拖平移;下面一条区间滑条(两头拖改范围、中间拖平移),
// 一排范围按钮(1 月、3 月、1 年、3 年、全部)和放大、缩小、复原。浮框和别的曲线的浮框一个样子(sbchart.ts 的 .sb-tip)。
// 数从引擎来(engine/web/src/stocks.rs 的 mestock:每一根 [哪天, 开, 高, 低, 收, 成交额, 有没有成交, 收盘按最近的成交价, 成交股数, 市值, 每股估值, 市盈率, 市净率])。
import { dateOf, esc, money, monthOf } from './sbchart.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type J = any;

export type KRange = 'day' | 'week' | 'month';
/** 看哪一家、哪一种线、看哪一段(kz 根,0 是全部;往前挪 ko 根)、涨跌的颜色。 */
export interface KView { firm: number | null; range: KRange; kz: number; ko: number; redUp: boolean }

/** 涨跌的颜色:中文界面默认红涨绿跌(暖红、绿,照 11-美术.md 的系列色),能换成绿涨红跌;记在这台浏览器上。 */
const KEY_UP = 'sb:kline:redUp';
export const loadRedUp = () => { try { return localStorage.getItem(KEY_UP) !== '0'; } catch { return true; } };
export const saveRedUp = (on: boolean) => { try { localStorage.setItem(KEY_UP, on ? '1' : '0'); } catch { /* 存不了就只管这一回 */ } };

const px = (v: number) => v >= 100 ? v.toFixed(0) : v.toFixed(2);
const nTxt = (n: number) => n < 10 ? n.toFixed(2) : n.toFixed(0);
const shareTxt = (n: number) => (n >= 1e4 ? money(n).replace(' 元', '') : nTxt(n));
const signed = (v: number, f: (v: number) => string) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${f(Math.abs(v))}`;
const pctS = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;

const W = 640, PW = 584, TOP = 8, PH = 190, VT = 206, VH = 46, H = 272;
/** 区间滑条的高。 */
const NH = 34;
/** 最少看几根。 */
const MINB = 5;
/** 一根是几天。 */
const BAR_DAYS: Record<KRange, number> = { day: 1, week: 7, month: 30 };
const SPANS: [string, number][] = [['1 月', 30], ['3 月', 90], ['1 年', 360], ['3 年', 1080]];
const RANGES: KRange[] = ['day', 'week', 'month'];
/** 均线几根:日线 5 日、20 日,周线 4 周、13 周,月线 3 个月、12 个月。 */
const MA: Record<KRange, [number, number, string, string]> = { day: [5, 20, '5 日', '20 日'], week: [4, 13, '4 周', '13 周'], month: [3, 12, '3 个月', '12 个月'] };
/** 一根的日子怎么写:月线写哪个月(月末那天收的是这个月),周线写哪天起的一周,日线写哪天。 */
const barDate = (d: number, range: KRange) => range === 'month' ? monthOf(Math.max(0, d - 1)) : range === 'week' ? `${dateOf(d)}起一周` : dateOf(d);
const short = (s: string) => s.replace(/^第 (\d+) 年 (\d+) 月(?: (\d+) 日)?(起一周)?$/, (_m, y, mo, d) => `${y}年${mo}月${d ? `${d}日` : ''}`);

/** 均线:只按真成交的收盘价算(最近 k 根有成交的平均,不满 k 根的不画);没成交的那几根不算也不画,免得看着像真行情。
 * 按整段历史算再截出看的那一段,缩放、平移以后还是同一条线。 */
const ma = (bars: J[], k: number) => {
  const win: number[] = [];
  return bars.map((x) => {
    if (!x[6]) return NaN;
    win.push(x[4]);
    if (win.length > k) win.shift();
    return win.length === k ? win.reduce((a, b) => a + b, 0) / k : NaN;
  });
};
/** 价格轴的刻度:挑 1、2、5 乘十的几次方,四五格。 */
const ticks = (lo: number, hi: number) => {
  const raw = (hi - lo) / 4 || 1;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((k) => k * p).find((s) => s >= raw) ?? 10 * p;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(v);
  return out;
};

/** 看的那一段:[起, 止) 和几根。 */
const windowOf = (n0: number, v: KView) => {
  const shown = v.kz > 0 ? Math.max(Math.min(MINB, n0), Math.min(v.kz, n0)) : n0;
  const end = n0 - Math.max(0, Math.min(v.ko, n0 - shown));
  return { from: end - shown, end, shown };
};
/** 改看的那一段(起、几根),收在整段历史里;看满了记成 0(全部)。 */
const setWindow = (v: KView, n0: number, from: number, shown: number) => {
  const s = Math.max(Math.min(MINB, n0), Math.min(n0, Math.round(shown)));
  const f = Math.max(0, Math.min(n0 - s, Math.round(from)));
  v.kz = s >= n0 ? 0 : s;
  v.ko = v.kz === 0 ? 0 : n0 - (f + s);
};

/** 鼠标(手机上手指)停在哪一根、选了哪一段:按日子记(刷新以后、新的一天进来,还在同一根上)。key 是公司和哪一种线。 */
let kHover: { key: string; day: number; y: number } | null = null;
let kSel: { key: string; a: number; b: number } | null = null;

/** 浮框放在那一根的哪边:在左半边的放右边,在右半边的放左边,不挡住那一根。 */
const tipPos = (i: number, n: number) => {
  const frac = (i + 0.5) / n * PW / W;
  return frac > 0.5 ? `right:calc(${((1 - frac) * 100).toFixed(1)}% + 14px)` : `left:calc(${(frac * 100).toFixed(1)}% + 14px)`;
};
/** 区间浮框:选的那一段右边有地方放右边,左边有地方放左边,都没有的贴左上角。 */
const selPos = (i0: number, i1: number, n: number) => {
  const a = i0 / n * PW / W, b = (i1 + 1) / n * PW / W;
  return b < 0.55 ? `left:calc(${(b * 100).toFixed(1)}% + 10px)` : a > 0.45 ? `right:calc(${((1 - a) * 100).toFixed(1)}% + 10px)` : 'left:8px';
};

/** 浮框里一行:名目、数。 */
const tipRow = (k: string, v: string, cls = '') => `<div>${k}<span${cls ? ` class="${cls}"` : ''}>${v}</span></div>`;
const upCls = (v: number) => (v > 0 ? 'kup' : v < 0 ? 'kdown' : '');

/** 悬停浮框:这一根的日子,开高低收(没成交的写收盘按什么)、涨跌、成交额和股数、当时的市值、估值(合计和每股)、市盈率、市净率。 */
const klineTip = (d: (number | null)[]) => {
  const [day, o, h, l, c, amt, traded, prev, rc, ref, qty, cap, val, pe, pb] = d as number[];
  const chg = prev > 0 ? c / prev - 1 : 0;
  const shares = cap != null && c > 0 ? cap / c : null;
  let s = `<b>${esc(barDate(day, RANGES[rc] ?? 'day'))}</b>`;
  if (!traded) s += `<em>没成交 · 收盘${ref === 1 ? '按最近的成交价' : '按估值'}</em>`;
  s += traded ? tipRow('开 · 收', `${px(o)} · ${px(c)} 元`) + tipRow('高 · 低', `${px(h)} · ${px(l)} 元`) : tipRow('收', `${px(c)} 元`);
  s += tipRow('涨跌', pctS(chg), upCls(chg));
  if (traded) s += tipRow('成交', `${money(amt)} · ${shareTxt(qty)} 股`);
  if (cap != null) s += tipRow('市值', money(cap));
  if (val != null) s += tipRow('估值', shares != null ? `${money(val * shares)} · 每股 ${px(val)}` : `每股 ${px(val)} 元`);
  if (pe != null || pb != null) s += tipRow('市盈率 · 市净率', `${pe != null ? pe.toFixed(1) : '—'} · ${pb != null ? pb.toFixed(2) : '—'}`);
  return s;
};

/** 区间浮框:起止、几天(几周、几个月)、起点价→终点价、累计涨跌、区间最高最低、成交合计、市值变了多少。
 * 起点价按区间前一根的收盘(第一根前面没有的按第一根的开盘)。 */
const selTip = (all: J[], g0: number, g1: number, range: KRange) => {
  const first = all[g0], last = all[g1];
  const start = g0 > 0 ? all[g0 - 1][4] : first[1];
  const end = last[4];
  const chg = end - start;
  let hi = -Infinity, lo = Infinity, amt = 0, qty = 0;
  for (let i = g0; i <= g1; i++) { hi = Math.max(hi, all[i][2]); lo = Math.min(lo, all[i][3]); amt += all[i][5] ?? 0; qty += all[i][8] ?? 0; }
  const span = range === 'day' ? `${last[0] - first[0] + 1} 天` : range === 'week' ? `${g1 - g0 + 1} 周` : `${g1 - g0 + 1} 个月`;
  const shares0 = first[9] != null && first[4] > 0 ? first[9] / first[4] : null;
  const cap0 = shares0 != null ? start * shares0 : null, cap1 = last[9];
  let s = `<b>${esc(short(barDate(first[0], range)))} – ${esc(short(barDate(last[0], range)))}</b><em>${span}</em>`;
  s += tipRow('起 → 止', `${px(start)} → ${px(end)} 元`);
  s += tipRow('累计', `${signed(chg, px)} 元 · ${start > 0 ? pctS(chg / start) : '—'}`, upCls(chg));
  s += tipRow('最高 · 最低', `${px(hi)} · ${px(lo)} 元`);
  s += tipRow('成交', amt > 0 ? `${money(amt)} · ${shareTxt(qty)} 股` : '没有');
  if (cap0 != null && cap1 != null) s += tipRow('市值', `${money(cap0)} → ${money(cap1)}(${signed(cap1 - cap0, money)})`, upCls(cap1 - cap0));
  return s;
};

/** 上面那一行:哪天(月线写哪个月)、开高低收、涨跌、成交;没成交的写收盘按什么。 */
const klineRow = (d: (number | null)[] | undefined) => {
  if (!d) return '';
  const [day, o, h, l, c, v, traded, prev, rc, ref] = d as number[];
  const chg = prev > 0 ? c / prev - 1 : 0;
  const when = esc(barDate(day, RANGES[rc] ?? 'day'));
  const how = ref === 1 ? ',按最近的成交价' : ref === 0 ? ',按估值' : '';
  return traded ? `<b>${when}</b> 开 ${px(o)} 高 ${px(h)} 低 ${px(l)} 收 ${px(c)} <span class="${upCls(chg)}">${pctS(chg)}</span> 成交 ${money(v)}`
    : `<b>${when}</b> 没成交${how} ${px(c)} <span class="${upCls(chg)}">${pctS(chg)}</span>`;
};

/** K 线整块:范围按钮、上面一行、图(带悬停浮框、区间浮框)、区间滑条、图例。 */
export const klineHTML = (bars: J[], v: KView) => {
  const all = bars;
  if (all.length < 2) return '<p class="sb-sd-note">还没有记录。</p>';
  const [k1, k2, n1, n2] = MA[v.range];
  const m1 = ma(all, k1), m2 = ma(all, k2);
  const n0 = all.length;
  const { from, end, shown } = windowOf(n0, v);
  const list = all.slice(from, end), a1 = m1.slice(from, end), a2 = m2.slice(from, end);
  const n = list.length, bw = PW / n;
  const vals = list.flatMap((x) => [x[2], x[3]]).concat(a1.filter(Number.isFinite), a2.filter(Number.isFinite));
  let hi = Math.max(...vals), lo = Math.min(...vals);
  const pad = (hi - lo) * 0.06 || hi * 0.05 || 1;
  hi += pad; lo = Math.max(0, lo - pad);
  const y = (val: number) => TOP + PH * (1 - (val - lo) / (hi - lo));
  const cx = (i: number) => i * bw + bw / 2;
  const up = v.redUp ? 'var(--s1)' : 'var(--s5)', down = v.redUp ? 'var(--s5)' : 'var(--s1)';
  const vmax = Math.max(1, ...list.map((x) => x[5]));
  const key = `${v.firm}:${v.range}`;
  let g = '';
  // 选的那一段:先铺一层底色
  let sel: [number, number] | null = null;
  if (kSel?.key === key) {
    const a = Math.min(kSel.a, kSel.b), b = Math.max(kSel.a, kSel.b);
    const i0 = list.findIndex((x) => x[0] >= a);
    let i1 = -1;
    for (let i = n - 1; i >= 0; i--) if (list[i][0] <= b) { i1 = i; break; }
    if (i0 >= 0 && i1 >= i0) {
      sel = [i0, i1];
      g += `<rect class="ksel" x="${(i0 * bw).toFixed(1)}" y="${TOP}" width="${((i1 - i0 + 1) * bw).toFixed(1)}" height="${VT + VH - TOP}"/>`;
    }
  }
  // 格线和价格轴
  for (const t of ticks(lo, hi)) g += `<line x1="0" x2="${PW}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" class="grid"/><text x="${PW + 4}" y="${(y(t) + 3).toFixed(1)}" class="t">${px(t)}</text>`;
  g += `<line x1="0" x2="${PW}" y1="${VT + VH}" y2="${VT + VH}" class="grid"/>`;
  // 没成交的几段:收盘价连成浅灰虚线(和真成交的分开)
  let seg: string[] = [];
  const flush = () => { if (seg.length > 1) g += `<polyline points="${seg.join(' ')}" class="est"/>`; else if (seg.length === 1) g += `<circle cx="${seg[0].split(',')[0]}" cy="${seg[0].split(',')[1]}" r="1.6" class="estd"/>`; seg = []; };
  list.forEach((x, i) => { if (x[6]) flush(); else seg.push(`${cx(i).toFixed(1)},${y(x[4]).toFixed(1)}`); });
  flush();
  // 蜡烛和成交额
  list.forEach((x, i) => {
    const [, o, h, l, c, amt, traded] = x;
    if (!traded) return;
    const prev = i > 0 ? list[i - 1][4] : (from > 0 ? all[from - 1][4] : o);
    const col = c > o || (c === o && c >= prev) ? up : down;
    const top = y(Math.max(o, c)), bot = y(Math.min(o, c));
    const w = Math.max(1, bw * 0.66);
    g += `<line x1="${cx(i).toFixed(1)}" x2="${cx(i).toFixed(1)}" y1="${y(h).toFixed(1)}" y2="${y(l).toFixed(1)}" style="stroke:${col}"/>`;
    g += `<rect x="${(cx(i) - w / 2).toFixed(1)}" y="${top.toFixed(1)}" width="${w.toFixed(1)}" height="${Math.max(1, bot - top).toFixed(1)}" style="fill:${col};stroke:${col}"/>`;
    if (amt > 0) { const vh = (VH - 2) * amt / vmax; g += `<rect x="${(cx(i) - w / 2).toFixed(1)}" y="${(VT + VH - vh).toFixed(1)}" width="${w.toFixed(1)}" height="${vh.toFixed(1)}" style="fill:${col}" opacity="0.55"/>`; }
  });
  // 均线:中间隔着没成交的就断开,一段一段画
  const line = (arr: number[], cls: string) => {
    let out = '', pts: string[] = [];
    const flushLine = () => { if (pts.length > 1) out += `<polyline points="${pts.join(' ')}" class="${cls}"/>`; pts = []; };
    arr.forEach((val, i) => { if (Number.isFinite(val)) pts.push(`${cx(i).toFixed(1)},${y(val).toFixed(1)}`); else flushLine(); });
    flushLine();
    return out;
  };
  g += line(a1, 'ma1') + line(a2, 'ma2');
  // 日期轴:五六个刻度
  const step = Math.max(1, Math.ceil(n / 5));
  for (let i = 0; i < n; i += step) g += `<line x1="${cx(i).toFixed(1)}" x2="${cx(i).toFixed(1)}" y1="${VT + VH}" y2="${VT + VH + 4}" class="grid"/><text x="${cx(i).toFixed(1)}" y="${H - 4}" class="t" text-anchor="${i === 0 ? 'start' : cx(i) > PW - 40 ? 'end' : 'middle'}">${esc(short(barDate(list[i][0], v.range)))}</text>`;
  if (list.some((x) => x[5] > 0)) g += `<text x="4" y="${VT + 10}" class="t">成交额 最高 ${money(vmax)}</text>`;
  // 每一根给鼠标用:日、开、高、低、收、成交额、有没有成交、前一根收盘、日周月、收盘按什么、成交股数、市值、每股估值、市盈率、市净率
  const rc = RANGES.indexOf(v.range);
  const data = list.map((x, i) => [x[0], x[1], x[2], x[3], x[4], x[5], x[6] ? 1 : 0, i > 0 ? list[i - 1][4] : (from > 0 ? all[from - 1][4] : x[1]), rc, x[7] ?? -1,
    x[8] ?? 0, x[9] ?? null, x[10] ?? null, x[11] ?? null, x[12] ?? null]);
  // 十字线和悬停浮框:停着的那一根(刷新以后还在)
  const at = kHover?.key === key ? list.findIndex((x) => x[0] === kHover!.day) : -1;
  const hx = at >= 0 ? cx(at).toFixed(1) : '0', hy = (kHover?.y ?? TOP).toFixed(1);
  g += `<g class="kx" visibility="${at >= 0 ? 'visible' : 'hidden'}"><line class="kxv" x1="${hx}" x2="${hx}" y1="${TOP}" y2="${VT + VH}"/><line class="kxh" x1="0" x2="${PW}" y1="${hy}" y2="${hy}"/></g>`;
  const read = data[at >= 0 ? at : n - 1];
  const tip = at >= 0 ? `<div class="sb-ktip" style="${tipPos(at, n)}">${klineTip(data[at])}</div>` : '<div class="sb-ktip" hidden></div>';
  const selBox = sel ? `<div class="sb-ktip sb-kseltip" style="${selPos(sel[0], sel[1], n)}">${selTip(all, from + sel[0], from + sel[1], v.range)}</div>` : '';
  // 范围按钮:按日线、周线、月线换成根数;根数不够的不出
  const bd = BAR_DAYS[v.range];
  const spans = SPANS.map(([t, days]) => [t, Math.ceil(days / bd)] as [string, number]).filter(([, k]) => k >= MINB && k < n0);
  const tools = `<div class="sb-ktools"><span class="sb-seg">${spans.map(([t, k]) => `<button class="${v.kz === k && v.ko === 0 ? 'on' : ''}" data-kact="span:${k}">${t}</button>`).join('')}<button class="${v.kz === 0 ? 'on' : ''}" data-kact="span:0">全部</button></span>
    <span class="sb-seg"><button data-kact="zoom:in" title="放大"${shown <= Math.min(MINB, n0) ? ' disabled' : ''}>放大</button><button data-kact="zoom:out" title="缩小"${v.kz === 0 ? ' disabled' : ''}>缩小</button><button data-kact="reset" title="复原">复原</button></span></div>`;
  // 区间滑条:整段历史的收盘价缩略线,看的那一段亮着,两头是把手
  const nx = (i: number) => (i / n0 * PW);
  const cl = all.map((x) => x[4]);
  const cmax = Math.max(...cl), cmin = Math.min(...cl);
  const ny = (val: number) => 4 + (NH - 8) * (1 - (val - cmin) / ((cmax - cmin) || 1));
  const nav = `<svg class="sb-knav" viewBox="0 0 ${W} ${NH}" data-key="${key}" data-total="${n0}" data-from="${from}" data-end="${end}">
    <rect class="bg" x="0" y="0" width="${PW}" height="${NH}"/>
    <polyline class="ln" points="${all.map((x, i) => `${(nx(i) + PW / n0 / 2).toFixed(1)},${ny(x[4]).toFixed(1)}`).join(' ')}"/>
    <rect class="dim" x="0" y="0" width="${nx(from).toFixed(1)}" height="${NH}"/><rect class="dim" x="${nx(end).toFixed(1)}" y="0" width="${(PW - nx(end)).toFixed(1)}" height="${NH}"/>
    <rect class="win" x="${nx(from).toFixed(1)}" y="0.5" width="${(nx(end) - nx(from)).toFixed(1)}" height="${NH - 1}"/>
    <rect class="hd" x="${(nx(from) - 3).toFixed(1)}" y="${NH / 2 - 9}" width="6" height="18" rx="2"/><rect class="hd" x="${(nx(end) - 3).toFixed(1)}" y="${NH / 2 - 9}" width="6" height="18" rx="2"/></svg>`;
  const legend = `<div class="sb-kline-legend"><span><i style="background:${up}"></i>涨</span><span><i style="background:${down}"></i>跌</span><span><i class="est"></i>没成交</span><span><i class="ma1"></i>${n1}均线</span><span><i class="ma2"></i>${n2}均线</span>
    <button class="sb-sd-btn" data-stk="redup:${v.redUp ? 0 : 1}">${v.redUp ? '红涨绿跌' : '绿涨红跌'} · 换</button><small>长按拖选一段看区间</small></div>`;
  return `<div class="sb-kbox">${tools}<div class="sb-kline-read" data-kline-read>${klineRow(read)}</div><div class="sb-kwrap"><svg class="sb-kline${v.redUp ? '' : ' greenup'}" viewBox="0 0 ${W} ${H}" data-k='${JSON.stringify(data)}' data-n="${n}" data-from="${from}" data-total="${n0}" data-key="${key}">${g}</svg>${tip}${selBox}</div>${nav}${legend}</div>`;
};

/** 鼠标在 K 线上动(手机上点一下):十字线挪到那一根,上面一行和浮框换成那一根的数(直接改,不重画整页)。 */
export const klineHover = (svg: SVGSVGElement, ev: { clientX: number; clientY: number }) => {
  const r = svg.getBoundingClientRect();
  const x = (ev.clientX - r.left) / r.width * W, yy = (ev.clientY - r.top) / r.height * H;
  const n = Number(svg.dataset.n ?? 0);
  const kx = svg.querySelector('.kx') as SVGGElement | null;
  if (!kx || !n) return;
  if (x < 0 || x > PW || yy < TOP || yy > VT + VH) { klineLeave(svg); return; }
  const i = Math.max(0, Math.min(n - 1, Math.floor(x / (PW / n))));
  const cxv = (i + 0.5) * PW / n;
  kx.setAttribute('visibility', 'visible');
  const v = kx.querySelector('.kxv')!, h = kx.querySelector('.kxh')!;
  v.setAttribute('x1', cxv.toFixed(1)); v.setAttribute('x2', cxv.toFixed(1));
  h.setAttribute('y1', yy.toFixed(1)); h.setAttribute('y2', yy.toFixed(1));
  const data = JSON.parse(svg.dataset.k ?? '[]') as (number | null)[][];
  kHover = { key: svg.dataset.key ?? '', day: data[i][0] as number, y: yy };
  const box = svg.closest('.sb-kbox');
  const read = box?.querySelector('[data-kline-read]');
  if (read) read.innerHTML = klineRow(data[i]);
  const tip = box?.querySelector('.sb-ktip:not(.sb-kseltip)') as HTMLElement | null;
  if (tip) { tip.innerHTML = klineTip(data[i]); tip.setAttribute('style', tipPos(i, n)); tip.hidden = false; }
};

/** 鼠标离开 K 线(手机上点了别处):十字线、浮框收起,上面一行回到最新一根。 */
export const klineLeave = (svg: SVGSVGElement) => {
  kHover = null;
  svg.querySelector('.kx')?.setAttribute('visibility', 'hidden');
  const data = JSON.parse(svg.dataset.k ?? '[]') as (number | null)[][];
  const box = svg.closest('.sb-kbox');
  const read = box?.querySelector('[data-kline-read]');
  if (read) read.innerHTML = klineRow(data[data.length - 1]);
  const tip = box?.querySelector('.sb-ktip:not(.sb-kseltip)') as HTMLElement | null;
  if (tip) tip.hidden = true;
};

/** 一次按下去的手势:等着看是拖(平移)还是长按(选区间),两根手指是捏合(缩放);区间滑条上是拖把手、拖那一段。 */
interface Gesture {
  on: 'chart' | 'nav'; key: string; id: number; x0: number; y0: number; touch: boolean;
  mode: 'wait' | 'pan' | 'select' | 'pinch' | 'navmove' | 'navleft' | 'navright';
  n0: number; from0: number; shown0: number; anchor: number; off: number; timer: number;
  d0: number; focus: number;
}
const LONG_MS = 300;
const MOVE_PX = 6;

/** 装上 K 线的鼠标、手指、滚轮、按钮(整页只装一回;图每次刷新重画,按 data-key 找回来)。
 * view() 给眼下看的(股市页的状态),redraw() 重画股市页(一帧最多一回)。 */
export const installKline = (view: () => KView, redraw: () => void) => {
  let raf = 0;
  const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; redraw(); }); };
  const chartOf = (key: string) => document.querySelector(`svg.sb-kline[data-key="${CSS.escape(key)}"]`) as SVGSVGElement | null;
  const navOf = (key: string) => document.querySelector(`svg.sb-knav[data-key="${CSS.escape(key)}"]`) as SVGSVGElement | null;
  /** 图上 x 处是看的那一段里第几根(0 起)。 */
  const barAt = (svg: SVGSVGElement, clientX: number) => {
    const r = svg.getBoundingClientRect();
    const n = Number(svg.dataset.n ?? 1);
    return Math.max(0, Math.min(n - 1, Math.floor((clientX - r.left) / r.width * W / (PW / n))));
  };
  /** 一根有几个屏幕像素宽。 */
  const perBar = (svg: SVGSVGElement, shown: number) => svg.getBoundingClientRect().width * PW / W / Math.max(1, shown);
  /** 以 frac(0~1,看的那一段里的位置)为中心缩放 factor 倍。 */
  const zoomAt = (n0: number, frac: number, factor: number) => {
    const v = view();
    const { from, shown } = windowOf(n0, v);
    const focus = from + frac * shown;
    const next = shown * factor;
    setWindow(v, n0, focus - frac * next, next);
  };
  const pts = new Map<number, { x: number; y: number }>();
  let g: Gesture | null = null;
  const clearSel = () => { if (kSel) { kSel = null; schedule(); } };

  document.addEventListener('click', (e) => {
    const b = (e.target as Element).closest?.('[data-kact]') as HTMLElement | null;
    if (!b) return;
    const box = b.closest('.sb-kbox');
    const svg = box?.querySelector('svg.sb-kline') as SVGSVGElement | null;
    if (!svg) return;
    const n0 = Number(svg.dataset.total ?? 0), v = view();
    const [k, arg] = (b.dataset.kact ?? '').split(':');
    if (k === 'span') { const s = Number(arg); if (s > 0) setWindow(v, n0, n0 - s, s); else { v.kz = 0; v.ko = 0; } }
    else if (k === 'zoom') zoomAt(n0, 0.5, arg === 'in' ? 0.6 : 1 / 0.6);
    else if (k === 'reset') { v.kz = 0; v.ko = 0; kSel = null; kHover = null; }
    redraw();
  });

  document.addEventListener('pointerdown', (e) => {
    const t = e.target as Element;
    const chart = t.closest?.('svg.sb-kline') as SVGSVGElement | null;
    const nav = t.closest?.('svg.sb-knav') as SVGSVGElement | null;
    // 选着一段:点别处(或者在图上重新按下去)就收起
    if (kSel && !t.closest?.('[data-kact]')) clearSel();
    // 手机上点了图以外的地方:悬停浮框收起
    if (e.pointerType !== 'mouse' && !chart) document.querySelectorAll<SVGSVGElement>('svg.sb-kline').forEach((s) => klineLeave(s));
    const svg = chart ?? nav;
    if (!svg) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const key = svg.dataset.key ?? '';
    const n0 = Number(svg.dataset.total ?? 0);
    const { from, shown } = windowOf(n0, view());
    // 第二根手指按下来:改成捏合缩放
    if (g && g.on === 'chart' && chart && pts.size === 2) {
      clearTimeout(g.timer);
      const [p1, p2] = [...pts.values()];
      const r = chart.getBoundingClientRect();
      g.mode = 'pinch';
      g.d0 = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
      g.focus = Math.max(0, Math.min(1, ((p1.x + p2.x) / 2 - r.left) / (r.width * PW / W)));
      g.from0 = from; g.shown0 = shown;
      return;
    }
    if (g) return;
    e.preventDefault();
    g = { on: chart ? 'chart' : 'nav', key, id: e.pointerId, x0: e.clientX, y0: e.clientY, touch: e.pointerType !== 'mouse', mode: 'wait', n0, from0: from, shown0: shown, anchor: 0, off: 0, timer: 0, d0: 0, focus: 0 };
    if (chart) {
      // 按住不动 0.3 秒:选区间,从按下去的那一根起
      g.timer = window.setTimeout(() => {
        if (!g || g.mode !== 'wait') return;
        const s = chartOf(g.key);
        if (!s) return;
        const data = JSON.parse(s.dataset.k ?? '[]') as number[][];
        const day = data[barAt(s, g.x0)]?.[0];
        if (day == null) return;
        g.mode = 'select';
        kHover = null;
        kSel = { key: g.key, a: day, b: day };
        schedule();
      }, LONG_MS);
    } else {
      // 区间滑条:按在把手上改范围,按在那一段里拖着平移,按在外面先把那一段挪过来
      const r = svg.getBoundingClientRect();
      const at = (e.clientX - r.left) / (r.width * PW / W) * n0;
      const end = from + shown, grip = 8 / (r.width * PW / W) * n0;
      if (Math.abs(at - from) <= grip) g.mode = 'navleft';
      else if (Math.abs(at - end) <= grip) g.mode = 'navright';
      else {
        g.mode = 'navmove';
        if (at < from || at > end) { setWindow(view(), n0, at - shown / 2, shown); schedule(); }
        g.off = at - windowOf(n0, view()).from;
      }
    }
  });

  document.addEventListener('pointermove', (e) => {
    if (pts.has(e.pointerId)) pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!g) {
      // 鼠标没按着:悬停
      if (e.pointerType !== 'mouse') return;
      const svg = (e.target as Element).closest?.('svg.sb-kline') as SVGSVGElement | null;
      if (svg) klineHover(svg, e);
      return;
    }
    const v = view();
    if (g.mode === 'pinch') {
      const s = chartOf(g.key);
      const p = [...pts.values()];
      if (!s || p.length < 2) return;
      const d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
      const next = g.shown0 * g.d0 / d;
      setWindow(v, g.n0, g.from0 + g.focus * g.shown0 - g.focus * next, next);
      schedule();
      return;
    }
    if (e.pointerId !== g.id) return;
    const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
    if (g.mode === 'wait' && Math.hypot(dx, dy) > MOVE_PX) { clearTimeout(g.timer); g.mode = 'pan'; }
    if (g.mode === 'pan') {
      const s = chartOf(g.key);
      if (!s) return;
      // 往右拖看早的(手抓着往右挪)
      setWindow(v, g.n0, g.from0 - dx / perBar(s, g.shown0), g.shown0);
      schedule();
    } else if (g.mode === 'select') {
      const s = chartOf(g.key);
      if (!s || !kSel) return;
      const data = JSON.parse(s.dataset.k ?? '[]') as number[][];
      const day = data[barAt(s, e.clientX)]?.[0];
      if (day != null && day !== kSel.b) { kSel.b = day; schedule(); }
    } else if (g.mode.startsWith('nav')) {
      const s = navOf(g.key);
      if (!s) return;
      const r = s.getBoundingClientRect();
      const at = Math.max(0, Math.min(g.n0, (e.clientX - r.left) / (r.width * PW / W) * g.n0));
      const { from, end } = windowOf(g.n0, v);
      if (g.mode === 'navmove') setWindow(v, g.n0, at - g.off, end - from);
      else if (g.mode === 'navleft') { const f = Math.min(at, end - MINB); setWindow(v, g.n0, f, end - f); }
      else { const en = Math.max(at, from + MINB); setWindow(v, g.n0, from, en - from); }
      schedule();
    }
  });

  const up = (e: PointerEvent) => {
    pts.delete(e.pointerId);
    if (!g) return;
    if (g.mode === 'pinch') { if (pts.size === 0) g = null; return; }
    if (e.pointerId !== g.id) return;
    clearTimeout(g.timer);
    // 手机上点一下(没拖、没长按):按下去的那一根出浮框,点别处收起
    if (g.mode === 'wait' && g.touch && g.on === 'chart' && e.type === 'pointerup') {
      const s = chartOf(g.key);
      if (s) klineHover(s, { clientX: g.x0, clientY: g.y0 });
    }
    g = null;
  };
  document.addEventListener('pointerup', up);
  document.addEventListener('pointercancel', up);
  // 鼠标移出图:浮框收起(手指抬起来也算移出,那个不管:手机上点别处才收)
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const svg = (e.target as Element).closest?.('svg.sb-kline') as SVGSVGElement | null;
    if (svg && !g && !svg.contains(e.relatedTarget as Node)) klineLeave(svg);
  });
  // 长按不出菜单(手机上长按会弹菜单、把这一按取消掉,拖不下去)
  document.addEventListener('contextmenu', (e) => {
    if ((e.target as Element).closest?.('svg.sb-kline, svg.sb-knav, .sb-cw[data-range]')) e.preventDefault();
  });

  // 滚轮、触控板:上下滚和捏合(ctrlKey)缩放,以鼠标那一根为中心;左右滑平移
  let carry = 0;
  document.body.addEventListener('wheel', (e) => {
    const t = e.target as Element;
    const svg = (t.closest?.('svg.sb-kline') ?? t.closest?.('svg.sb-knav')) as SVGSVGElement | null;
    if (!svg) return;
    e.preventDefault();
    const n0 = Number(svg.dataset.total ?? 0), v = view();
    const { from, shown } = windowOf(n0, v);
    const r = svg.getBoundingClientRect();
    if (e.ctrlKey || Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
      const frac = svg.classList.contains('sb-kline') ? Math.max(0, Math.min(1, (e.clientX - r.left) / (r.width * PW / W))) : 0.5;
      zoomAt(n0, frac, Math.exp(e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)));
    } else {
      carry += e.deltaX / (r.width * PW / W / Math.max(1, svg.classList.contains('sb-kline') ? shown : n0));
      const step = Math.trunc(carry);
      if (!step) return;
      carry -= step;
      setWindow(v, n0, from + step, shown);
    }
    schedule();
  }, { passive: false });
};
